import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { motion } from "motion/react"
import { useRequest } from "../../hooks/useRequest"
import { userStore } from "../../store/userStore"
import { Ticket } from "../../types/entities/ticket"
import { TicketItem } from "../../features/tickets/TicketItem"
import TicketsEmptyHero from "../../features/tickets/TicketsEmptyHero"
import PageTitle from "../../components/PageTitle"
import { isUnpaid } from "../../features/tickets/ticketUtils"
import { clearOrderPaymentMethod } from "../../lib/orderPaymentMethodStorage"
import { unpaidOrderStore } from "../../store/unpaidOrderStore"

const MONO = "'JetBrains Mono', monospace"

export default function Tickets() {
    const { request } = useRequest()
    const { user } = userStore()
    const navigate = useNavigate()
    const location = useLocation()
    const highlightOrderId = (location.state as { highlightOrderId?: string } | null)?.highlightOrderId
    const [tickets, setTickets] = useState<Ticket[]>([])
    const [loading, setLoading] = useState(true)
    const [cancelLoading, setCancelLoading] = useState<string | number | null>(null)
    const [highlightedId, setHighlightedId] = useState<string | undefined>(highlightOrderId)

    const getTickets = async () => {
        if (!user) return
        const data = await request<Ticket[]>("GET", "user/order")
        const list = Array.isArray(data) ? data : []
        setTickets(list)
        const unpaid = list.find(isUnpaid) ?? null
        unpaidOrderStore.getState().setUnpaidTicket(unpaid)
        if (unpaid) unpaidOrderStore.getState().markSeen(unpaid.id)
    }

    const cancel = async (ticketId: string | number, statusId: string | number) => {
        setCancelLoading(ticketId)
        try {
            await request(statusId === 2 ? "DELETE" : "PATCH", `user/order/${ticketId}`)
            clearOrderPaymentMethod(String(ticketId))
            await getTickets()
        } finally {
            setCancelLoading(null)
        }
    }

    const ticketsRef = useRef<Ticket[]>([])
    ticketsRef.current = tickets

    useEffect(() => {
        if (!highlightOrderId || loading) return
        const timer = window.setTimeout(() => {
            document.getElementById(`ticket-${highlightOrderId}`)?.scrollIntoView({ behavior: "smooth", block: "center" })
        }, 150)
        const fadeTimer = window.setTimeout(() => setHighlightedId(undefined), 4000)
        return () => {
            window.clearTimeout(timer)
            window.clearTimeout(fadeTimer)
        }
    }, [highlightOrderId, loading, tickets.length])

    useEffect(() => {
        if (!user) {
            setLoading(false)
            return
        }
        setLoading(true)
        getTickets().finally(() => setLoading(false))

        const intervalId = setInterval(() => {
            const unpaidTicket = ticketsRef.current.find(isUnpaid)
            if (unpaidTicket) getTickets()
        }, 5000)

        return () => clearInterval(intervalId)
    }, [user])

    if (!user) {
        return <TicketsEmptyHero variant="guest" onLogin={() => navigate("/auth")} />
    }

    if (loading) {
        return (
            <div className="flex justify-center py-16 pb-28 md:pb-6">
                <motion.div
                    className="size-10 rounded-full border-2 border-white/10 border-t-[#349be6]"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.8, ease: "linear", repeat: Infinity }}
                />
            </div>
        )
    }

    if (tickets.length === 0) {
        return (
            <div className="pt-16 pb-20 md:pb-4">
                <div className="mx-auto max-w-[1240px] px-4 pt-2 pb-3 sm:px-8">
                    <PageTitle>Мои билеты</PageTitle>
                </div>
                <TicketsEmptyHero variant="empty" />
            </div>
        )
    }

    return (
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 px-4 pt-16 pb-28 md:py-6 md:pb-6">
            <div className="flex items-end justify-between gap-3 pt-2 pb-1">
                <PageTitle>Мои билеты</PageTitle>
                <span className="text-white/35 text-[12px]" style={{ fontFamily: MONO, fontWeight: 700 }}>
                    {tickets.length}
                </span>
            </div>

            <div className="flex flex-col gap-4">
                {tickets.map((item, index) => (
                    <TicketItem
                        key={item.id}
                        ticket={item}
                        index={index}
                        onCancel={cancel}
                        cancelLoading={cancelLoading}
                        highlighted={highlightedId === item.id}
                        onRefresh={getTickets}
                    />
                ))}
            </div>
        </div>
    )
}
